import type { ConnectionHealth } from '../components/connections/ConnectionRow';

/**
 * Turning a connection's raw fields into the one word a vendor needs.
 *
 * Kept out of the page component so the rules are testable and so the row and
 * the drawer can never disagree about whether something is broken.
 */

/** Warn this far ahead of a token dying — long enough to act, short enough not to nag. */
export const TOKEN_EXPIRY_WARNING_DAYS = 7;

export function daysUntil(date: Date | string | null | undefined): number | null {
  if (!date) return null;
  const ms = new Date(date).getTime() - Date.now();
  return Math.floor(ms / 86_400_000);
}

/**
 * True when a connection's access token is gone or about to be.
 *
 * The token-refresh worker renews embedded-signup and Instagram-login tokens
 * automatically, so a token that is genuinely close to expiry usually means a
 * manual/BYOC or legacy connection that nothing is watching — the case where
 * the vendor finds out by their messages stopping.
 */
export function tokenExpiring(tokenExpiresAt: Date | string | null | undefined): boolean {
  const days = daysUntil(tokenExpiresAt);
  return days !== null && days <= TOKEN_EXPIRY_WARNING_DAYS;
}

export function expiryDetail(tokenExpiresAt: Date | string | null | undefined): string | null {
  const days = daysUntil(tokenExpiresAt);
  if (days === null || days > TOKEN_EXPIRY_WARNING_DAYS) return null;
  if (days < 0) return 'Your connection has expired — reconnect to keep messaging.';
  if (days === 0) return 'Your connection expires today — reconnect to keep messaging.';
  return `Your connection expires in ${days} day${days === 1 ? '' : 's'} — reconnect to keep messaging.`;
}

export interface ChannelHealthInput {
  connected: boolean;
  /** WhatsApp only: Meta hasn't finished verifying the number. */
  pending?: boolean;
  /** Inbound is proven to be wired up. */
  receiving: boolean;
  tokenExpiresAt?: Date | string | null;
}

/**
 * Order matters, and it is the order in which a vendor loses service:
 * disconnected, then still-provisioning, then can't-receive, then
 * about-to-expire.
 */
export function channelHealth(input: ChannelHealthInput): ConnectionHealth {
  if (!input.connected) return 'disconnected';
  if (input.pending) return 'pending';
  if (!input.receiving) return 'attention';
  if (tokenExpiring(input.tokenExpiresAt)) return 'attention';
  return 'working';
}
